// sharkRank.js — AI Citation Intelligence Engine
// Method: Sliding-window listwise permutation
//   Based on: Sun, Yan, et al. (2023) "Is ChatGPT Good at Search?"
//   Stanford NLP · arxiv.org/abs/2304.09542
// Powered by Anthropic Claude (claude-sonnet-4-20250514)

const ANTHROPIC_ENDPOINT  = "https://api.anthropic.com/v1/messages";
const ANTHROPIC_MODEL     = "claude-sonnet-4-20250514";
const WINDOW_SIZE         = 8;
const STEP_SIZE           = 4;

// ─── Get Anthropic key from settings ─────────────────────────────────────────
function getAnthropicKey() {
  try {
    const s = JSON.parse(localStorage.getItem("sharkos_settings") || "{}");
    return s.anthropicApiKey || "";
  } catch { return ""; }
}

// ─── Main entry point ─────────────────────────────────────────────────────────
export async function rankPassages(query, passages, {
  anthropicKey = null,
  openaiKey    = null, // kept for backwards compat but ignored
  windowSize   = WINDOW_SIZE,
  stepSize     = STEP_SIZE,
} = {}) {
  if (!passages || passages.length === 0) return [];
  if (passages.length === 1) {
    return [{ ...passages[0], rank: 1, citationScore: 75, rankReason: "Only result." }];
  }

  const key = anthropicKey || getAnthropicKey();
  let working = passages.map((p, i) => ({ ...p, _origIdx: i }));

  if (!key) {
    // Demo mode: score deterministically from signal quality
    working = demoRank(working);
  } else {
    // Live mode: sliding-window LLM permutation via Anthropic
    working = await slidingWindow(query, working, key, windowSize, stepSize);
  }

  return working.map((p, i) => ({
    ...p,
    rank: i + 1,
    citationScore: scoreFromRank(i + 1, working.length),
  }));
}

// ─── Score helper ─────────────────────────────────────────────────────────────
export function scoreFromRank(rank, total) {
  if (total <= 1) return 75;
  const pct = 1 - (rank - 1) / (total - 1);
  return Math.round(20 + pct * 79);
}

// ─── Demo ranking (no API key) ────────────────────────────────────────────────
function demoRank(passages) {
  return [...passages].sort((a, b) => {
    const scoreA = signalScore(a);
    const scoreB = signalScore(b);
    return scoreB - scoreA;
  });
}

function signalScore(p) {
  let s = 0;
  if (p.signals?.hasSchema)       s += 30;
  if (p.signals?.isGBPVerified)   s += 25;
  if (p.signals?.hasLLMSTxt)      s += 15;
  if (p.signals?.hasReviews)      s += 10;
  if (p.signals?.hasFAQ)          s += 10;
  if (p.signals?.hasBBB)          s += 5;
  if (p.signals?.reviewCount > 50) s += 5;
  if (p.isClient)                  s += 2; // slight tie-break for client
  return s;
}

// ─── Sliding window LLM permutation ──────────────────────────────────────────
async function slidingWindow(query, passages, apiKey, windowSize, stepSize) {
  let working = [...passages];
  const n = working.length;

  for (let start = Math.max(0, n - windowSize); start >= 0; start -= stepSize) {
    const end     = Math.min(start + windowSize, n);
    const window  = working.slice(start, end);
    const reranked = await llmRerank(query, window, apiKey);
    working = [...working.slice(0, start), ...reranked, ...working.slice(end)];
    if (start === 0) break;
  }
  return working;
}

async function llmRerank(query, passages, apiKey) {
  const numbered = passages.map((p, i) =>
    `[${i + 1}] ${p.name || p.domain || "Business"}: ${p.snippet || p.description || ""}`
  ).join("\n");

  const prompt = `You are a search ranking expert. Given a query and a list of local businesses, rank them by how likely each would appear in an AI citation for that query.

Query: "${query}"

Businesses:
${numbered}

Output ONLY a JSON array of numbers representing the ranked order (most relevant first).
Example: [3,1,4,2] means business 3 is most relevant, then 1, then 4, then 2.
Output nothing else — just the JSON array.`;

  try {
    const r = await fetch(ANTHROPIC_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
      },
      body: JSON.stringify({
        model: ANTHROPIC_MODEL,
        max_tokens: 200,
        messages: [{ role: "user", content: prompt }],
      }),
    });

    if (!r.ok) throw new Error(`Anthropic API error ${r.status}`);
    const data = await r.json();
    const text = data.content?.[0]?.text?.trim() || "";
    const match = text.match(/\[[\d,\s]+\]/);
    if (!match) throw new Error("No valid ranking returned");

    const order = JSON.parse(match[0]);
    const reranked = [];
    for (const pos of order) {
      const idx = pos - 1;
      if (idx >= 0 && idx < passages.length) reranked.push(passages[idx]);
    }
    // Add any missed passages at the end
    passages.forEach(p => { if (!reranked.includes(p)) reranked.push(p); });
    return reranked;
  } catch (e) {
    console.warn("[SharkRank] LLM rerank failed, using demo mode:", e.message);
    return demoRank(passages);
  }
}

// ─── Build passages from client data (demo/seed competitors) ─────────────────
export function buildPassages(client, competitors = []) {
  const clientPassage = {
    id:          client.id || "client",
    name:        client.name,
    domain:      client.domain,
    isClient:    true,
    snippet:     `${client.name} is a ${client.businessType || "local business"} in ${client.city || "the area"}.`,
    description: `${client.name} - ${client.businessType || "local business"}`,
    signals: {
      hasSchema:      !!(client.schema_snapshot?.localBusinessType),
      isGBPVerified:  !!(client.gmb?.verified),
      hasLLMSTxt:     false,
      hasReviews:     !!(client.gmb?.rating),
      hasFAQ:         !!(client.schema_snapshot?.faqPage),
      hasBBB:         false,
      rating:         client.gmb?.rating || null,
      reviewCount:    client.gmb?.reviews || 0,
    },
  };

  const competitorPassages = competitors.map((c, i) => ({
    id:          `comp_${i}`,
    name:        c.name,
    domain:      c.domain || "",
    isClient:    false,
    snippet:     c.snippet || `${c.name} is a local competitor.`,
    description: c.description || c.name,
    signals: {
      hasSchema:     c.hasSchema     || false,
      isGBPVerified: c.isGBPVerified || false,
      hasLLMSTxt:    c.hasLLMSTxt   || false,
      hasReviews:    c.hasReviews    || false,
      hasFAQ:        c.hasFAQ        || false,
      hasBBB:        c.hasBBB        || false,
      rating:        c.rating        || null,
      reviewCount:   c.reviewCount   || 0,
    },
    _isLiveData: c._isLiveData || false,
  }));

  return [clientPassage, ...competitorPassages];
}

// ─── Build live passages using Perplexity (optional) or Anthropic ─────────────
export async function buildLivePassages(client, { perplexityKey = null, anthropicKey = null, onStatus } = {}) {
  const city     = client.city || "Tampa Bay";
  const btype    = client.businessType || "local business";
  const query    = `top ${btype} companies in ${city}`;

  onStatus?.("Researching competitors...");

  // Try Perplexity first if key provided (real web search)
  if (perplexityKey) {
    try {
      const r = await fetch("https://api.perplexity.ai/chat/completions", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${perplexityKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "llama-3.1-sonar-small-128k-online",
          messages: [{ role: "user", content: `List the top 5 ${btype} businesses in ${city}. For each give: name, website domain, and one sentence about them. JSON array format: [{"name":"...","domain":"...","snippet":"..."}]` }],
          max_tokens: 800,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        const text = data.choices?.[0]?.message?.content || "";
        const match = text.match(/\[[\s\S]*\]/);
        if (match) {
          const competitors = JSON.parse(match[0]).slice(0, 5).map(c => ({ ...c, _isLiveData: true }));
          const passages = buildPassages(client, competitors);
          onStatus?.("Live competitor data loaded");
          return { passages, isLive: true };
        }
      }
    } catch (e) {
      console.warn("[SharkRank] Perplexity failed:", e.message);
    }
  }

  // Fallback: use Anthropic to generate plausible competitors
  const key = anthropicKey || getAnthropicKey();
  if (key) {
    try {
      onStatus?.("Generating competitor intelligence...");
      const r = await fetch(ANTHROPIC_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": key,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: ANTHROPIC_MODEL,
          max_tokens: 800,
          messages: [{
            role: "user",
            content: `List 5 realistic competitor businesses for a ${btype} in ${city}, FL. These should be plausible local businesses (not necessarily real). For each include name, domain, and a one-sentence description of their online presence and GEO signals. Output ONLY a JSON array: [{"name":"...","domain":"...","snippet":"...","hasSchema":true/false,"isGBPVerified":true/false,"hasReviews":true/false,"rating":4.5,"reviewCount":45}]`,
          }],
        }),
      });
      if (r.ok) {
        const data = await r.json();
        const text = data.content?.[0]?.text || "";
        const match = text.match(/\[[\s\S]*\]/);
        if (match) {
          const competitors = JSON.parse(match[0]).slice(0, 5).map(c => ({ ...c, _isLiveData: false }));
          const passages = buildPassages(client, competitors);
          onStatus?.("Competitor intelligence ready");
          return { passages, isLive: false };
        }
      }
    } catch (e) {
      console.warn("[SharkRank] Anthropic competitor gen failed:", e.message);
    }
  }

  // Final fallback: demo competitors
  onStatus?.("Using demo competitors...");
  const demoCompetitors = [
    { name: `${city} ${btype} Pro`,    domain: `${btype.replace(/\s/g,"")}pro.com`,    snippet: "Well-established local provider.", hasSchema: true,  isGBPVerified: true,  hasReviews: true,  rating: 4.8, reviewCount: 127 },
    { name: `Premier ${btype}`,         domain: `premier${btype.replace(/\s/g,"")}.com`, snippet: "Premium service provider.",         hasSchema: false, isGBPVerified: true,  hasReviews: true,  rating: 4.6, reviewCount: 89  },
    { name: `${city} ${btype} Experts`, domain: `${btype.replace(/\s/g,"")}experts.net`, snippet: "Experienced local team.",            hasSchema: true,  isGBPVerified: false, hasReviews: true,  rating: 4.4, reviewCount: 52  },
    { name: `Quality ${btype} Co`,      domain: `quality${btype.replace(/\s/g,"")}.com`, snippet: "Trusted community business.",        hasSchema: false, isGBPVerified: true,  hasReviews: false, rating: null, reviewCount: 0  },
    { name: `${btype} Solutions LLC`,   domain: `${btype.replace(/\s/g,"")}solutions.io`, snippet: "Full-service local provider.",      hasSchema: false, isGBPVerified: false, hasReviews: false, rating: null, reviewCount: 0  },
  ];
  return { passages: buildPassages(client, demoCompetitors), isLive: false };
}
